import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { record } from "rrweb";
import { API } from "../App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Monitor, ShieldAlert, XCircle } from "lucide-react";

const WS_URL = (path) => {
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  return `${backend.replace(/^http/, "ws")}${path}`;
};

const CHUNK = 200000;

/**
 * Globalno vidno v Layout: LD uporabnik dobi popup, ko SA zahteva live view.
 * rrweb DOM mirroring prek WebSocket (deluje tudi na iOS/Android) s chunking,
 * ping keepalive in samodejnim ponovnim povezovanjem.
 */
const ScreenMirrorPrompt = ({ token, user }) => {
  const [pending, setPending] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [controlled, setControlled] = useState(false);
  const wsRef = useRef(null);
  const stopRef = useRef(null);
  const pingRef = useRef(null);
  const activeRef = useRef(false);
  const retryRef = useRef(0);

  useEffect(() => {
    if (!token || !user?.id) return;
    const headers = { Authorization: `Bearer ${token}` };
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/sa/screen-mirror/pending`, { headers });
        if (res.data.pending && !sharing) setPending(res.data.pending);
      } catch { /* silent */ }
    };
    poll();
    const iv = setInterval(poll, 4000);
    return () => clearInterval(iv);
  }, [token, user?.id, sharing]);

  const reject = async () => {
    if (!pending) return;
    try {
      await axios.post(`${API}/sa/screen-mirror/respond`, { session_id: pending.id, action: "reject" }, { headers: { Authorization: `Bearer ${token}` } });
    } catch { /* silent */ }
    setPending(null);
  };

  const sendEvent = (ws, event) => {
    const s = JSON.stringify(event);
    if (s.length <= CHUNK) {
      ws.send(JSON.stringify({ type: "rrweb", data: event }));
      return;
    }
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const total = Math.ceil(s.length / CHUNK);
    for (let i = 0; i < total; i++) {
      ws.send(JSON.stringify({ type: "rrweb_chunk", id, seq: i, total, part: s.slice(i * CHUNK, (i + 1) * CHUNK) }));
    }
  };

  const showClickRipple = (x, y) => {
    const dot = document.createElement("div");
    dot.style.cssText = `position:fixed;left:${x - 14}px;top:${y - 14}px;width:28px;height:28px;border:3px solid #dc2626;border-radius:9999px;z-index:2147483647;pointer-events:none;opacity:0.9;transition:all .5s ease-out;background:rgba(220,38,38,0.15)`;
    document.body.appendChild(dot);
    requestAnimationFrame(() => { dot.style.transform = "scale(1.8)"; dot.style.opacity = "0"; });
    setTimeout(() => dot.remove(), 550);
  };

  const setNativeValue = (el, value) => {
    const proto = el.tagName === "TEXTAREA" ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
    const setter = Object.getOwnPropertyDescriptor(proto, "value")?.set;
    if (setter) setter.call(el, value); else el.value = value;
    el.dispatchEvent(new Event("input", { bubbles: true }));
  };

  const insertAtCursor = (el, text) => {
    const start = el.selectionStart ?? (el.value || "").length;
    const end = el.selectionEnd ?? (el.value || "").length;
    setNativeValue(el, (el.value || "").slice(0, start) + text + (el.value || "").slice(end));
    try { el.setSelectionRange(start + text.length, start + text.length); } catch { /* ignore */ }
  };

  const deleteAtCursor = (el, dir) => {
    const v = el.value || "";
    let start = el.selectionStart ?? v.length;
    let end = el.selectionEnd ?? v.length;
    if (start === end) {
      if (dir < 0) start = Math.max(0, start - 1);
      else end = Math.min(v.length, end + 1);
    }
    setNativeValue(el, v.slice(0, start) + v.slice(end));
    try { el.setSelectionRange(start, start); } catch { /* ignore */ }
  };

  const execKey = (msg) => {
    const el = document.activeElement;
    const kOpts = { bubbles: true, cancelable: true, key: msg.key, ctrlKey: !!msg.ctrl, shiftKey: !!msg.shift, altKey: !!msg.alt, metaKey: !!msg.meta };
    let notPrevented = true;
    if (el) notPrevented = el.dispatchEvent(new KeyboardEvent("keydown", kOpts));
    const editable = el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA") && !el.readOnly && !el.disabled;
    if (editable && !msg.ctrl && !msg.meta) {
      if (msg.key && msg.key.length === 1) insertAtCursor(el, msg.key);
      else if (msg.key === "Backspace") deleteAtCursor(el, -1);
      else if (msg.key === "Delete") deleteAtCursor(el, 1);
      else if (msg.key === "Enter") {
        if (el.tagName === "TEXTAREA") insertAtCursor(el, "\n");
        else if (notPrevented && el.form) { try { el.form.requestSubmit(); } catch { /* ignore */ } }
      }
    } else if (el?.isContentEditable && msg.key?.length === 1) {
      try { document.execCommand("insertText", false, msg.key); } catch { /* ignore */ }
    }
    if (el) el.dispatchEvent(new KeyboardEvent("keyup", kOpts));
  };

  const execControl = (msg) => {
    try {
      if (msg.kind === "click") {
        const x = Math.round((msg.fracX || 0) * window.innerWidth);
        const y = Math.round((msg.fracY || 0) * window.innerHeight);
        showClickRipple(x, y);
        const el = document.elementFromPoint(x, y);
        if (!el) return;
        const mouseOpts = { bubbles: true, cancelable: true, clientX: x, clientY: y, view: window };
        const ptrOpts = { ...mouseOpts, pointerId: 1, pointerType: "mouse", isPrimary: true, button: 0, buttons: 1 };
        try { el.dispatchEvent(new PointerEvent("pointerdown", ptrOpts)); } catch { /* ignore */ }
        el.dispatchEvent(new MouseEvent("mousedown", mouseOpts));
        try { el.dispatchEvent(new PointerEvent("pointerup", { ...ptrOpts, buttons: 0 })); } catch { /* ignore */ }
        el.dispatchEvent(new MouseEvent("mouseup", mouseOpts));
        el.dispatchEvent(new MouseEvent("click", mouseOpts));
        if (typeof el.focus === "function") { try { el.focus(); } catch { /* ignore */ } }
      } else if (msg.kind === "scroll") {
        const target = document.elementFromPoint(
          Math.round((msg.fracX ?? 0.5) * window.innerWidth),
          Math.round((msg.fracY ?? 0.5) * window.innerHeight)
        );
        let node = target;
        while (node && node !== document.body) {
          const oy = getComputedStyle(node).overflowY;
          if ((oy === "auto" || oy === "scroll") && node.scrollHeight > node.clientHeight) {
            node.scrollBy({ top: msg.dy || 0, left: msg.dx || 0 });
            return;
          }
          node = node.parentElement;
        }
        window.scrollBy({ top: msg.dy || 0, left: msg.dx || 0 });
      } else if (msg.kind === "back") {
        window.history.back();
      } else if (msg.kind === "key") {
        execKey(msg);
      }
    } catch { /* ignore */ }
  };

  const connectLd = (sessionId) => {
    const ws = new WebSocket(WS_URL(`/api/sa/screen-mirror/ws/${sessionId}?role=ld&token=${token}`));
    wsRef.current = ws;

    ws.onopen = () => {
      retryRef.current = 0;
      setSharing(true);
      try { stopRef.current?.(); } catch { /* ignore */ }
      stopRef.current = record({
        emit(event) {
          if (ws.readyState === WebSocket.OPEN) sendEvent(ws, event);
        },
        sampling: { mousemove: 40, scroll: 100, media: 800, input: "last" },
        inlineImages: false,
        collectFonts: false,
        recordCanvas: false,
      });
      if (pingRef.current) clearInterval(pingRef.current);
      pingRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "ping" }));
      }, 25000);
    };

    ws.onmessage = (ev) => {
      const msg = JSON.parse(ev.data);
      if (msg.type === "peer_joined") {
        try { record.takeFullSnapshot(true); } catch { /* ignore */ }
      } else if (msg.type === "bye") {
        endShare();
      } else if (msg.type === "control_start") {
        setControlled(true);
      } else if (msg.type === "control_stop") {
        setControlled(false);
      } else if (msg.type === "control") {
        execControl(msg);
      }
      // peer_left: SA se lahko ponovno poveže – deljenje ostane aktivno
    };

    ws.onclose = () => {
      if (pingRef.current) clearInterval(pingRef.current);
      if (!activeRef.current) return;
      if (retryRef.current < 15) {
        retryRef.current += 1;
        setTimeout(() => { if (activeRef.current) connectLd(sessionId); }, 3000);
      } else {
        endShare();
      }
    };
  };

  const accept = async () => {
    if (!pending) return;
    try {
      await axios.post(`${API}/sa/screen-mirror/respond`, { session_id: pending.id, action: "accept" }, { headers: { Authorization: `Bearer ${token}` } });
      activeRef.current = true;
      retryRef.current = 0;
      connectLd(pending.id);
      setPending(null);
      toast.info("Sprejeto — vzpostavljam povezavo ...");
    } catch (e) {
      toast.error("Deljenje ni uspelo: " + (e?.message || "neznano"));
      setPending(null);
    }
  };

  const endShare = () => {
    activeRef.current = false;
    setControlled(false);
    try { stopRef.current?.(); } catch { /* ignore */ }
    stopRef.current = null;
    if (pingRef.current) clearInterval(pingRef.current);
    try {
      if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send(JSON.stringify({ type: "bye" }));
      wsRef.current?.close();
    } catch { /* ignore */ }
    wsRef.current = null;
    setSharing(false);
  };

  useEffect(() => { return () => endShare(); /* eslint-disable-next-line */ }, []);

  return (
    <>
      {pending && (
        <Dialog open={true} onOpenChange={(o) => { if (!o) reject(); }}>
          <DialogContent data-testid="screen-mirror-prompt">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldAlert className="h-5 w-5 text-amber-600" />
                Zahteva za pogled zaslona
              </DialogTitle>
              <DialogDescription>
                <b>{pending.sa_ime || "Super-admin"}</b> želi v živo videti vaš pogled aplikacije (za podporo/nastavitve) in lahko po dogovoru tudi <b>klika in drsa</b> po vašem zaslonu (daljinska pomoč). Vidna je samo ta aplikacija, nič drugega na napravi. Delitev lahko kadarkoli ustavite.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={reject} data-testid="mirror-prompt-reject">Zavrni</Button>
              <Button onClick={accept} data-testid="mirror-prompt-accept">
                <Monitor className="h-4 w-4 mr-2" />Sprejmi in deli
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {sharing && (
        <div
          className={`fixed bottom-24 right-4 z-[10000] text-white rounded-lg shadow-lg px-3 py-2 flex items-center gap-2 text-sm ${controlled ? "bg-amber-600" : "bg-red-600"}`}
          data-testid="mirror-active-indicator"
        >
          <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
          {controlled ? "Super-admin upravlja vaš zaslon" : "Zaslon deljen s SA"}
          <button onClick={endShare} className="ml-2 hover:opacity-70" title="Ustavi" data-testid="mirror-stop-share"><XCircle className="h-4 w-4" /></button>
        </div>
      )}
    </>
  );
};

export default ScreenMirrorPrompt;
